import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { CountryOption } from '../data/countries';

type SearchableCountrySelectProps = {
  id?: string;
  countries: CountryOption[];
  value: CountryOption | null;
  onChange: (country: CountryOption | null) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
};

export function SearchableCountrySelect({
  id,
  countries,
  value,
  onChange,
  placeholder = 'Search or select country',
  error,
  disabled,
}: SearchableCountrySelectProps) {
  const listId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const displayLabel = value ? `${value.name}` : '';

  useEffect(() => {
    if (value) setQuery(value.name);
    else if (!open) setQuery('');
  }, [value, open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter(
      (c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)
    );
  }, [countries, query]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const borderClass = error ? 'border-danger' : 'border-border';

  return (
    <div ref={wrapRef} className="relative w-full">
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          disabled={disabled}
          placeholder={placeholder}
          value={open ? query : displayLabel}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
            onChange(null);
          }}
          onFocus={() => {
            setOpen(true);
            setQuery(value?.name ?? '');
          }}
          className={`w-full rounded-lg border bg-navy px-3 py-2.5 pr-9 text-sm text-foreground outline-none transition placeholder:text-muted focus:border-accent focus:ring-1 focus:ring-accent/40 ${borderClass}`}
          autoComplete="off"
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted" aria-hidden>
          ▾
        </span>
      </div>

      {open ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-border bg-surface py-1 shadow-lg"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-sm text-muted">No matches</li>
          ) : (
            filtered.map((c) => (
              <li key={c.code} role="option" aria-selected={value?.code === c.code}>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-sm text-foreground transition hover:bg-foreground/5"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onChange(c);
                    setQuery(c.name);
                    setOpen(false);
                  }}
                >
                  {c.name}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}

      {error ? <p className="mt-1.5 text-sm text-danger">{error}</p> : null}
    </div>
  );
}
